import React, { useState, useEffect, useCallback } from 'react';
import { Plus, ArrowLeft, Megaphone } from 'lucide-react';
import { apiGetCampaigns, apiCreateCampaign } from '../../api';
import CampaignEditor from './CampaignEditor';
import { StatusBadge, Empty, ActionButton, Toast, useToast, formatDate } from './parts';
import styles from './Mailer.module.css';

/**
 * Campaign list, and the editor for whichever one is selected. A starter
 * sequence is seeded on create so a new campaign opens with a working
 * three-step structure rather than an empty form.
 */
/** Sum of a campaign's active allocations — what it actually sends in a day. */
const perDay = (campaign) => {
  const allocations = (campaign.mailboxAllocations || []).filter((a) => a.active !== false);
  if (!allocations.length) return campaign.schedule?.dailyCap ?? '—';
  return allocations.reduce((sum, a) => sum + (a.dailyCap || 0), 0);
};

/** "3 domains" beats listing three addresses in a table cell. */
const sendingFrom = (campaign) => {
  const count = (campaign.mailboxAllocations || []).filter((a) => a.active !== false).length;
  if (!count) return campaign.fromAddress || 'not set';
  if (count === 1) return campaign.fromAddress || '1 mailbox';
  return `${count} mailboxes`;
};

const CampaignsTab = ({ jwtToken, mailboxes = [], defaultFrom }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const report = useToast();
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetCampaigns(jwtToken);
      setCampaigns(data.campaigns || []);
    } catch (e) {
      setError(e.message || 'Could not load campaigns');
    } finally {
      setLoading(false);
    }
  }, [jwtToken]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    setError('');
    try {
      // Seed the new campaign with one mailbox at a conservative 20/day. A
      // campaign that opens with no sender assigned cannot be started and gives
      // no clue why; one that opens claiming every mailbox would silently eat
      // the capacity another campaign is relying on. One address, modest share,
      // adjust from there.
      const seed = mailboxes.find((m) => m.address === defaultFrom) || mailboxes[0];

      const { campaign } = await apiCreateCampaign(jwtToken, {
        name: newName.trim(),
        fromAddress: seed?.address || '',
        ...(seed ? { mailboxAllocations: [{ mailbox: seed._id, dailyCap: Math.min(20, seed.dailyLimit ?? 20) }] } : {}),
        steps: [
          {
            waitDays: 0,
            subject: 'Quick question about {{company}}',
            body: 'Hi {{firstName|there}},\n\nI came across {{company}} and wanted to ask — [[are you|would you be]] open to a short conversation about it?\n\nIf now is not the time, just say so and I will leave it there.',
            active: true,
          },
          {
            waitDays: 3,
            subject: '',
            body: 'Hi {{firstName|there}},\n\nFloating this back to the top of your inbox in case it got buried.\n\nWorth a quick look?',
            active: true,
          },
          {
            waitDays: 5,
            subject: '',
            body: 'Hi {{firstName|there}},\n\nI will stop here so I am not cluttering your inbox. If the timing changes, my door is open.\n\nAll the best.',
            active: true,
          },
        ],
      });
      setNewName('');
      setShowNew(false);
      await load();
      setSelected(campaign._id);
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  if (selected) {
    return (
      <div>
        <button className="btn btn-ghost btn-sm" onClick={() => { setSelected(null); load(); }} style={{ marginBottom: 16 }}>
          <ArrowLeft size={15} /> All campaigns
        </button>
        <CampaignEditor
          jwtToken={jwtToken}
          campaignId={selected}
          mailboxes={mailboxes}
          onChanged={load}
          onArchived={() => { setSelected(null); load(); }}
        />
      </div>
    );
  }

  return (
    <div>
      <Toast message={report.toast?.message} tone={report.toast?.tone} onDismiss={report.clear} />

      <div className={styles.toolbar}>
        <div className={styles.spacer} />
        <button className="btn btn-primary" onClick={() => setShowNew((v) => !v)}>
          <Plus size={16} /> New campaign
        </button>
      </div>

      {error && <div className={styles.dangerBox}>{error}</div>}

      {showNew && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 className="card-title" style={{ marginBottom: 12 }}>Name this campaign</h3>
          <div className={styles.toolbar} style={{ marginBottom: 0 }}>
            <input
              className="input"
              style={{ maxWidth: 340 }}
              placeholder="e.g. Author outreach — Q3"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && create()}
              autoFocus
            />
            <ActionButton
              className="btn btn-primary"
              onClick={create}
              disabled={!newName.trim()}
              busyLabel="Creating…"
              doneLabel="Created"
              failLabel="Failed"
            >
              Create
            </ActionButton>
            <button className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
          <p className="text-sm text-muted" style={{ marginTop: 12, marginBottom: 0 }}>
            Starts as a draft with a three-step sequence you can rewrite. Nothing sends until you
            enroll contacts and press Start.
          </p>
        </div>
      )}

      {loading ? (
        <div className="empty"><span className="spinner" style={{ width: 30, height: 30 }} /></div>
      ) : campaigns.length === 0 ? (
        <Empty icon={<Megaphone size={40} color="var(--text-muted)" />} title="No campaigns yet">
          A campaign is a sequence of emails plus the rules for how slowly they go out.
          Create one to get started.
        </Empty>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Campaign</th><th>Status</th><th>Steps</th><th>Enrolled</th>
                <th>Sent</th><th>Replies</th><th>Per day</th><th>Sending from</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const counts = c.enrollmentCounts || {};
                const inFlight = (counts.pending || 0) + (counts.active || 0);
                return (
                  <tr key={c._id} onClick={() => setSelected(c._id)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td className={styles.cellMuted}>{c.steps?.length || 0}</td>
                    <td>{inFlight} <span className={styles.cellMuted}>/ {c.stats?.enrolled || 0}</span></td>
                    <td>{c.stats?.sent || 0}</td>
                    <td style={{ color: c.stats?.replied ? 'var(--success)' : undefined, fontWeight: c.stats?.replied ? 600 : 400 }}>
                      {c.stats?.replied || 0}
                    </td>
                    <td className={styles.cellMuted}>{perDay(c)}</td>
                    <td className={styles.cellMuted}>{sendingFrom(c)}</td>
                    <td className={styles.cellMuted}>{formatDate(c.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CampaignsTab;
